/**
 * The bottom tab bar's measured footprint, composed in one place.
 *
 * theme.css (:root) declares the bar's default geometry; the mobile web/PWA
 * shell overrides it on the shell root. Both the bar and every screen's scroll
 * clearance read those vars, so they agree by construction inside the shell
 * subtree.
 *
 * Outside it they did not. Anything portalled to document.body (the toaster,
 * for one) sees only the :root defaults, so it composed a 55px + 34px bar while
 * the real one was 80px + 34px and drew itself inside the tab row. The composed
 * height below is therefore SELF-CONTAINED wherever the shell overrides the
 * geometry: literal terms, no `var(--qs-mobile-bottom-nav-min-height)`
 * indirection, so the same string means the same number at :root as it does on
 * the shell root. Only the genuinely dynamic terms (the home-indicator inset,
 * the mobile-browser chrome inset) stay as vars.
 */
import type { ShellRuntime } from '../../layout-system/platformLayouts';

/** Republished on the document element so portalled surfaces can read it. */
export const MOBILE_BOTTOM_NAV_HEIGHT_VAR = '--qs-mobile-bottom-nav-height';

/** Content zone of the mobile web/PWA bar, above the home-indicator floor. */
const MATERIAL_CONTENT_HEIGHT = '80px';
const MATERIAL_VERTICAL_PADDING = '0px';
/**
 * Installed PWAs can report env(safe-area-inset-bottom) as 0 while the home
 * indicator still owns the bottom of the screen, so the iPhone floor is a
 * hard 34px. Other runtimes only need a small resting gap.
 */
const IOS_SAFE_FLOOR = 'max(var(--safe-area-bottom, 0px), 34px)';
const DEFAULT_SAFE_FLOOR = 'max(var(--safe-area-bottom, 0px), 8px)';
/** Safari's bottom address bar; the whole bar is lifted by it in browser tabs. */
const BROWSER_CHROME = 'var(--qs-browser-chrome-bottom, 0px)';

export interface MobileBottomNavGeometryInput {
  /** False on the surfaces that never paint the bar (desktop, native iOS). */
  isRendered: boolean;
  /** Mobile web and installed PWAs; native shells keep their own chrome. */
  usesMaterialChrome: boolean;
  shellRuntime: ShellRuntime;
}

export interface MobileBottomNavGeometry {
  /** Shell-root override, or undefined to keep the :root default. */
  minHeight: string | undefined;
  verticalPadding: string | undefined;
  safeFloor: string | undefined;
  /** Total reserved height: content zone + indicator floor + browser chrome. */
  height: string;
}

export function composeMobileBottomNavGeometry({
  isRendered,
  usesMaterialChrome,
  shellRuntime,
}: MobileBottomNavGeometryInput): MobileBottomNavGeometry {
  const minHeight = usesMaterialChrome ? MATERIAL_CONTENT_HEIGHT : undefined;
  const verticalPadding = usesMaterialChrome ? MATERIAL_VERTICAL_PADDING : undefined;
  const safeFloor = usesMaterialChrome
    ? shellRuntime === 'ios'
      ? IOS_SAFE_FLOOR
      : DEFAULT_SAFE_FLOOR
    : undefined;

  const contentTerm = minHeight ?? 'var(--qs-mobile-bottom-nav-min-height)';
  const floorTerm = safeFloor ?? 'var(--qs-mobile-bottom-nav-safe-floor)';

  return {
    minHeight,
    verticalPadding,
    safeFloor,
    height: isRendered
      ? `calc(${contentTerm} + ${floorTerm} + ${BROWSER_CHROME})`
      : '0px',
  };
}
